import { useState, useEffect, useRef, useMemo } from "react";
import { Header } from "@/components/header";
import { Footer } from "@/components/footer";
import { useGalleryImages, type GalleryImage } from "@/hooks/use-supabase-data";
import { ImageLightbox } from "@/components/image-lightbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Image as ImageIcon, ChevronLeft, ChevronRight, Grid3x3, LayoutGrid, Folder, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMediaQuery } from "@/hooks/use-mobile";

export default function Gallery() {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const autoplayIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const { images, loading } = useGalleryImages();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // All unique categories (unfiltered) for the filter chips
  const allCategories = useMemo(() => {
    if (!images) return [];
    const cats = Array.from(new Set(images.map((img) => img.category || 'Uncategorized'))).sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });
    return cats;
  }, [images]);

  // Apply search and category filter before grouping
  const filteredImages = useMemo(() => {
    if (!images) return [];
    const q = searchQuery.toLowerCase().trim();
    return images.filter((img) => {
      const matchesCategory = selectedCategory === 'All' || (img.category || 'Uncategorized') === selectedCategory;
      const matchesSearch =
        !q ||
        (img.title && img.title.toLowerCase().includes(q)) ||
        (img.description && img.description.toLowerCase().includes(q)) ||
        (img.category && img.category.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });
  }, [images, selectedCategory, searchQuery]);

  // Group filtered images by category
  const imagesByCategory = useMemo(() => {
    const grouped: Record<string, GalleryImage[]> = {};

    filteredImages.forEach((image) => {
      const category = image.category || 'Uncategorized';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(image);
    });

    const sortedCategories = Object.keys(grouped).sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });

    const sorted: Record<string, GalleryImage[]> = {};
    sortedCategories.forEach((cat) => { sorted[cat] = grouped[cat]; });
    return sorted;
  }, [filteredImages]);

  // Flatten filtered images for lightbox and carousel
  const allImages = useMemo(() => {
    return Object.values(imagesByCategory).flat();
  }, [imagesByCategory]);
  
  // Track if user has manually toggled view mode
  const [hasUserToggled, setHasUserToggled] = useState(false);
  
  // Default to grid on desktop, carousel on mobile
  // Use isMobile to determine initial state (will be updated by useEffect if needed)
  const [viewMode, setViewMode] = useState<'carousel' | 'grid'>(() => {
    if (typeof window !== 'undefined') {
      // Default to carousel on mobile, grid on desktop
      return window.innerWidth < 768 ? 'carousel' : 'grid';
    }
    return 'carousel'; // Default to carousel for SSR (safer for mobile-first)
  });
  
  // Update view mode when screen size changes (only if user hasn't manually toggled)
  const prevIsMobile = useRef(isMobile);
  
  useEffect(() => {
    // Only update if screen size actually changed (not on initial mount)
    if (prevIsMobile.current !== isMobile) {
      prevIsMobile.current = isMobile;
      if (!hasUserToggled) {
        const newMode = isMobile ? 'carousel' : 'grid';
        setViewMode(newMode);
      }
    } else if (!hasUserToggled) {
      // On initial mount, ensure correct mode based on isMobile
      const correctMode = isMobile ? 'carousel' : 'grid';
      if (viewMode !== correctMode) {
        setViewMode(correctMode);
      }
    }
  }, [isMobile, hasUserToggled, viewMode]);

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
  };

  const handleNavigate = (index: number) => {
    setLightboxIndex(index);
  };

  // Reset carousel position when filters change
  useEffect(() => {
    setCurrentSlide(0);
  }, [selectedCategory, searchQuery]);

  // Auto-play for carousel
  useEffect(() => {
    if (!autoplay || allImages.length === 0 || viewMode !== 'carousel') return;

    autoplayIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % allImages.length);
    }, 4000);

    return () => {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
      }
    };
  }, [isMobile, autoplay, allImages.length, viewMode]);

  const handlePrevious = () => {
    setAutoplay(false);
    setCurrentSlide((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  const handleNext = () => {
    setAutoplay(false);
    setCurrentSlide((prev) => (prev + 1) % allImages.length);
  };

  const handleSlideClick = (index: number) => {
    setCurrentSlide(index);
    setAutoplay(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <div className="text-center mb-6">
            <h1 className="font-poppins text-4xl md:text-5xl font-bold text-foreground mb-2">
              Gallery
            </h1>
            <p className="font-inter text-muted-foreground text-lg">
              Explore our collection of images and moments
            </p>
          </div>

          {/* Search bar */}
          {!loading && (images?.length ?? 0) > 0 && (
            <div className="relative max-w-lg mx-auto mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search by title or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          )}

          {/* Category filter chips */}
          {!loading && allCategories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-4">
              <Button
                variant={selectedCategory === 'All' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('All')}
                className="rounded-full"
              >
                All
              </Button>
              {allCategories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className="rounded-full"
                >
                  {cat}
                </Button>
              ))}
            </div>
          )}

          {/* View Toggle Button */}
          {!loading && allImages.length > 0 && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => {
                  setHasUserToggled(true);
                  setViewMode(viewMode === 'carousel' ? 'grid' : 'carousel');
                }}
                className="neo-button"
              >
                {viewMode === 'carousel' ? (
                  <>
                    <Grid3x3 className="w-4 h-4 mr-2" />
                    View All
                  </>
                ) : (
                  <>
                    <LayoutGrid className="w-4 h-4 mr-2" />
                    Carousel View
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        {/* Gallery by Category */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-square rounded-2xl overflow-hidden neo-card">
                <Skeleton className="w-full h-full" />
              </div>
            ))}
          </div>
        ) : (images?.length ?? 0) === 0 ? (
          <div className="text-center py-20">
            <ImageIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="font-inter text-muted-foreground text-lg">
              No images in gallery yet.
            </p>
          </div>
        ) : allImages.length === 0 ? (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="font-inter text-muted-foreground text-lg">No images match your search.</p>
            <Button
              variant="ghost"
              onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
              className="mt-2"
            >
              Clear filters
            </Button>
          </div>
        ) : (
          <>
            {/* Carousel View - Shows all images */}
            {viewMode === 'carousel' && (
              <div className="relative">
                <MobileCarousel
                  images={allImages}
                  currentSlide={currentSlide}
                  onPrevious={handlePrevious}
                  onNext={handleNext}
                  onSlideClick={handleSlideClick}
                  onImageClick={(index) => {
                    const clickedImage = allImages[index];
                    const globalIndex = allImages.findIndex(img => img.id === clickedImage.id);
                    handleImageClick(globalIndex);
                  }}
                />
              </div>
            )}

            {/* Grid View - Shows images by category */}
            {viewMode === 'grid' && (
              <div className="space-y-12">
                {Object.entries(imagesByCategory).map(([category, categoryImages]) => (
                  <CategorySection
                    key={category}
                    category={category}
                    images={categoryImages}
                    allImages={allImages}
                    onImageClick={handleImageClick}
                  />
                ))}
              </div>
            )}

            {/* Lightbox */}
            <ImageLightbox
              images={allImages}
              currentIndex={lightboxIndex}
              isOpen={isLightboxOpen}
              onClose={handleCloseLightbox}
              onNavigate={handleNavigate}
            />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

interface GalleryImageCardProps {
  image: GalleryImage;
  index: number;
  onClick: () => void;
}

interface MobileCarouselProps {
  images: GalleryImage[];
  currentSlide: number;
  onPrevious: () => void;
  onNext: () => void;
  onSlideClick: (index: number) => void;
  onImageClick: (index: number) => void;
}

function MobileCarousel({
  images,
  currentSlide,
  onPrevious,
  onNext,
  onSlideClick,
  onImageClick,
}: MobileCarouselProps) {
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      onNext();
    }
    if (isRightSwipe) {
      onPrevious();
    }
  };

  const getSlideStyle = (index: number) => {
    const offset = index - currentSlide;
    const absOffset = Math.abs(offset);
    
    // Calculate rotation and scale based on position
    let rotateY = 0;
    let scale = 1;
    let translateX = 0;
    let zIndex = 10;
    let opacity = 1;

    if (offset === 0) {
      // Center image - enlarged and no rotation
      scale = 1.1;
      zIndex = 20;
    } else if (offset === -1) {
      // Left side - rotate right
      rotateY = 25;
      scale = 0.85;
      translateX = -30;
      zIndex = 15;
      opacity = 0.7;
    } else if (offset === 1) {
      // Right side - rotate left
      rotateY = -25;
      scale = 0.85;
      translateX = 30;
      zIndex = 15;
      opacity = 0.7;
    } else {
      // Further away - more rotation and smaller
      rotateY = offset > 0 ? -35 : 35;
      scale = 0.6;
      translateX = offset * 50;
      zIndex = 5;
      opacity = 0.3;
    }

    return {
      transform: `perspective(1000px) rotateY(${rotateY}deg) scale(${scale}) translateX(${translateX}%)`,
      zIndex,
      opacity,
      transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s ease',
    };
  };

  return (
    <div 
      className="relative w-full h-[45vh] md:h-[60vh] overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative w-full h-full" style={{ perspective: '1000px' }}>
        {images.map((image, index) => {
          const offset = index - currentSlide;
          const isVisible = Math.abs(offset) <= 2; // Only show 5 images at a time

          if (!isVisible) return null;

          return (
            <div
              key={image.id}
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              style={getSlideStyle(index)}
              onClick={() => {
                if (offset === 0) {
                  onImageClick(index);
                } else {
                  onSlideClick(index);
                }
              }}
            >
              <div className="w-[80%] max-w-sm aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={image.image_url}
                  alt="Gallery image"
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/50 text-white h-10 w-10"
            onClick={(e) => {
              e.stopPropagation();
              onPrevious();
            }}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-30 bg-black/30 hover:bg-black/50 text-white h-10 w-10"
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </>
      )}

      {/* Slide Indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'w-8 bg-white'
                  : 'w-2 bg-white/50'
              }`}
              onClick={() => onSlideClick(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface CategorySectionProps {
  category: string;
  images: GalleryImage[];
  allImages: GalleryImage[];
  onImageClick: (index: number) => void;
}

function CategorySection({ category, images, allImages, onImageClick }: CategorySectionProps) {
  return (
    <section className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 neo-icon-box">
          <Folder className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="font-poppins text-2xl md:text-3xl font-bold text-foreground">
            {category}
          </h2>
          <p className="font-inter text-sm text-muted-foreground">
            {images.length} image{images.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((image) => {
          const globalIndex = allImages.findIndex(img => img.id === image.id);
          return (
            <div key={image.id}>
              <GalleryImageCard
                image={image}
                index={globalIndex}
                onClick={() => onImageClick(globalIndex)}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function GalleryImageCard({ image, onClick }: GalleryImageCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <div
      className="group relative aspect-square rounded-2xl overflow-hidden neo-card cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
      onClick={onClick}
    >
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      {imageError ? (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <ImageIcon className="w-8 h-8 text-muted-foreground" />
        </div>
      ) : (
        <img
          src={image.image_url}
          alt="Gallery image"
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            imageLoaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageError(true);
            setImageLoaded(false);
          }}
          loading="lazy"
        />
      )}
    </div>
  );
}

